import { useState } from "react";

import { setStringAsync } from "expo-clipboard";
import { downloadAsync, getInfoAsync } from "expo-file-system/legacy";
import { isAvailableAsync, shareAsync } from "expo-sharing";
import isNil from "lodash/isNil";
import { Alert } from "react-native";

import { DocumentType } from "@/constants/Documents";
import useAppMonitor from "@/services/AppMonitor";
import {
  encodeDocumentPath,
  getDocumentTypeFromFileName,
  getFileExtension,
} from "@/utils/documents.util";
import { isAndroid, isIOS, isWeb } from "@/utils/platform.util";

interface ShareFileParams {
  fileUrl: string;
  onError?: (error: Error) => void;
  title: string;
}

interface ShareDocumentParams {
  fileUrl: string;
  title: string;
}

// Mirrors equip-vesta and my-equip's download mechanism
const triggerWebDownload = async ({
  fileName,
  fileUrl,
}: {
  fileName: string;
  fileUrl: string;
}): Promise<void> => {
  if (!isWeb()) return;
  let blobURL: null | string = null;
  try {
    const response = await fetch(fileUrl, {
      headers: { "Cache-Control": "no-cache" },
    });
    const data = await response.arrayBuffer();
    blobURL = globalThis.URL.createObjectURL(new Blob([data]));
    const link = globalThis.document.createElement("a");
    link.href = blobURL;
    link.setAttribute("download", fileName);
    globalThis.document.body.appendChild(link);
    link.click();
    globalThis.document.body.removeChild(link);
  } finally {
    // intentionally *not* returning from this 'finally' clause as that would silently discard any thrown error
    if (!isNil(blobURL)) globalThis.URL.revokeObjectURL(blobURL);
  }
};

const shareUrl = async (params: ShareDocumentParams) => {
  const { fileUrl, title } = params;

  try {
    await shareAsync(fileUrl, {
      dialogTitle: `Share ${title}`,
    });
    return true;
  } catch (error) {
    throw new Error(`Unable to share the URL ${error}`);
  }
};

const verifyDownloadedFile = async (uri: string) => {
  const fileInfo = await getInfoAsync(uri);
  if (!fileInfo.exists) {
    throw new Error(`Downloaded file does not exist at path: ${uri}`);
  }

  if (fileInfo.size === 0) {
    throw new Error(`Downloaded file is empty at path: ${uri}`);
  }
};

const shareDocument = async (params: ShareDocumentParams) => {
  const { fileUrl, title } = params;
  const extension = getFileExtension(fileUrl);
  if (!extension) {
    throw new Error(`Unable to determine the file extension for ${fileUrl}`);
  }

  const newPath = encodeDocumentPath({ extension, title });
  if (!newPath) {
    throw new Error("Unable to generate a path for the file");
  }

  const { uri } = await downloadAsync(fileUrl, newPath);

  // Verify the file exists and is accessible before sharing
  await verifyDownloadedFile(uri);

  // Share the downloaded file
  await shareAsync(uri, {
    dialogTitle: `Share ${title}`,
  });

  return true;
};

const useShare = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { captureException } = useAppMonitor();

  const shareWeb = async ({ fileUrl, onError, title }: ShareFileParams) => {
    try {
      setIsLoading(true);
      await triggerWebDownload({ fileName: title, fileUrl });
    } catch (error) {
      captureException(error, { context: { fileUrl, title } });
      globalThis.window.open(fileUrl, "_blank", "noopener,noreferrer");
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- legitimate use case
      onError?.(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const shareNative = async ({ fileUrl, onError, title }: ShareFileParams) => {
    const isSharingAvailable = await isAvailableAsync();
    if (!isSharingAvailable) {
      // Fall back to copying the URL to the clipboard if sharing is not available
      await setStringAsync(fileUrl);
      // Android shows a custom clipboard system toast automatically
      if (isIOS()) {
        Alert.alert("Resource link was copied to clipboard");
      }
      return;
    }

    try {
      setIsLoading(true);
      const isGenericDocument =
        getDocumentTypeFromFileName(fileUrl) === DocumentType.Generic;

      if (isGenericDocument && !isAndroid()) {
        await shareUrl({ fileUrl, title });
      } else {
        await shareDocument({ fileUrl, title });
      }
    } catch (error) {
      captureException(error);
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- legitimate use case
      onError?.(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const shareFile = async (params: ShareFileParams) => {
    // on web, expo-sharing doesn't exist; so the file is downloaded instead
    if (isWeb()) {
      await shareWeb(params);
      return;
    }
    await shareNative(params);
  };

  return { isLoading, shareFile };
};

export default useShare;
